// Reminders engine — pure. Stored reminders live in `reminders/{id}` (per user). ATTACHED reminders are not
// stored: they are derived from what they belong to (an event's remind[], a block's / task's remindAt) so
// they move when it moves. `settings.reminders` (meta/settings) will hold per-kind defaults later; today it
// is read but empty, so nothing fires that you did not ask for.
import { blockMinutes } from './commands/blocks.js';
import { expandRepeats } from './calendar.js';
import { routineRings, routineOfVirtual } from './routines.js';

export const REMINDER_STYLES = ['banner', 'fullscreen', 'alarm'];
export const SNOOZE_DEFAULT = [5, 10, 30, 60];
export const REMINDER_STYLE_LABELS = { banner: 'Notification', fullscreen: 'Full screen', alarm: 'Alarm' };

/** "at the time" · "45 min before" · "1 h 30 min before" · "2 days before" — any whole number of minutes. */
export function minutesBeforeLabel(m) {
  const n = Math.max(0, Math.round(Number(m) || 0));
  if (n === 0) return 'at the time';
  if (n % 1440 === 0) return `${n / 1440} day${n === 1440 ? '' : 's'} before`;
  const h = Math.floor(n / 60); const min = n % 60;
  return `${[h ? `${h} h` : null, min ? `${min} min` : null].filter(Boolean).join(' ')} before`;
}

/** Add (or re-style) an attached reminder rule; one rule per offset, sorted soonest-to-the-start last. */
export function upsertRemindRule(remind, minutesBefore, style = 'banner') {
  const mb = Math.max(0, Math.round(Number(minutesBefore) || 0));
  const st = REMINDER_STYLES.includes(style) ? style : 'banner';
  const rest = (Array.isArray(remind) ? remind : []).filter((r) => Math.round(Number(r.minutesBefore) || 0) !== mb);
  return [...rest, { minutesBefore: mb, style: st }].sort((a, b) => b.minutesBefore - a.minutesBefore);
}
const midnight = (date) => new Date(`${date}T00:00:00`).getTime();

/**
 * Reminder settings (meta/settings.reminders; none built in): per-kind defaults — { event: { call: [{minutesBefore,
 * style}], … }, block: [...], task: [...] } — plus `style` (new reminders) and `snooze` (the minutes offered on a ring).
 */
export function reminderDefaults(settings) {
  const r = settings?.reminders || {};
  const snooze = Array.isArray(r.snooze) ? r.snooze.map(Number).filter((n) => n > 0).sort((a, b) => a - b) : [];
  return {
    event: r.event || {}, block: Array.isArray(r.block) ? r.block : [], task: Array.isArray(r.task) ? r.task : [],
    style: REMINDER_STYLES.includes(r.style) ? r.style : 'banner',
    snooze: snooze.length ? snooze : SNOOZE_DEFAULT,
  };
}

/** Virtual reminders for events / blocks / tasks in [from, to] (dates). */
export function deriveAttached({ events = [], blocks = [], tasks = [], settings = null, from, to, routines = [], routineRuns = [], uid = null }) {
  const d = reminderDefaults(settings);
  const out = [];
  // Routines ring by their schedule, with their own look (routines.js routineRings); a started / done / skipped run silences its slot.
  if (routines.length) out.push(...routineRings(routines, routineRuns, { from, to, uid }));
  for (const e of expandRepeats(events, from, to)) {
    if (e.status && e.status !== 'planned') continue;
    const rules = Array.isArray(e.remind) ? e.remind : (d.event[e.kind] || []);
    const startMs = e.allDay || !e.start ? midnight(e.date) + 9 * 3600000 : e.start;
    for (const r of rules) {
      const mb = Number(r.minutesBefore) || 0;
      out.push({ id: `ev:${e.id}:${mb}`, virtual: true, title: e.title, body: [e.customerName, e.address].filter(Boolean).join(' · ') || null, at: startMs - mb * 60000, style: r.style || 'banner', link: { type: 'event', id: e.occurrenceOf || e.id }, minutesBefore: mb, status: 'scheduled', source: 'calendar' });
    }
  }
  for (const b of expandRepeats(blocks, from, to)) {
    const rules = Array.isArray(b.remind) ? b.remind : (b.remindAt != null ? [{ minutesBefore: b.remindAt }] : d.block);
    const [start] = blockMinutes(b);
    for (const r of rules) {
      const mb = Number(r.minutesBefore) || 0;
      out.push({ id: `bl:${b.id}:${mb}`, virtual: true, title: b.title || 'Project block', at: midnight(b.date) + start * 60000 - mb * 60000, style: r.style || 'banner', link: { type: 'block', id: b.occurrenceOf || b.id }, minutesBefore: mb, status: 'scheduled', source: 'calendar' });
    }
  }
  const lo = midnight(from); const hi = midnight(to) + 86400000;
  for (const t of tasks) {
    // A task with a reminder time IS a reminder: it fires then, whatever day the task is planned for.
    if (t.status === 'todo' && Number.isFinite(t.remindTime) && t.remindTime >= lo && t.remindTime < hi) {
      out.push({ id: `tk:${t.id}:at`, virtual: true, title: t.title, body: t.description || null, at: t.remindTime, style: REMINDER_STYLES.includes(t.remindStyle) ? t.remindStyle : 'banner', link: t.opens?.type && t.opens?.id ? { type: t.opens.type, id: t.opens.id } : { type: 'task', id: t.id }, taskId: t.id, taskReminder: true, status: 'scheduled', source: 'task' });
    }
  }
  for (const t of tasks) {
    if (t.status !== 'todo' || !t.scheduledDate || t.scheduledDate < from || t.scheduledDate > to) continue;
    const rules = Array.isArray(t.remind) ? t.remind : (t.remindAt != null ? [{ minutesBefore: t.remindAt }] : d.task);
    if (!rules.length) continue;
    const start = Number.isFinite(t.plannedStart) ? t.plannedStart : 9 * 60;
    for (const r of rules) {
      const mb = Number(r.minutesBefore) || 0;
      out.push({ id: `tk:${t.id}:${mb}`, virtual: true, title: t.title, at: midnight(t.scheduledDate) + start * 60000 - mb * 60000, style: r.style || 'banner', link: { type: 'task', id: t.id }, minutesBefore: mb, status: 'scheduled', source: 'calendar' });
    }
  }
  return out;
}

/** Everything due to fire in [fromMs, toMs]: stored reminders (with repeats) + attached, soonest first. */
export function nextOccurrences({ reminders = [], attached = [], fromMs, toMs }) {
  const out = [];
  for (const r of reminders) {
    if (!r || ['done', 'cancelled'].includes(r.status)) continue;
    if (r.source === 'approval' && approvalStatus(r) !== 'pending') continue;   // decided: nothing left to ring for
    const at = r.status === 'snoozed' && r.snoozedUntil ? r.snoozedUntil : r.at;
    if (!r.repeat?.freq) { if (at >= fromMs && at <= toMs) out.push({ ...r, at }); continue; }
    const every = Math.max(1, Number(r.repeat.every) || 1);
    const step = r.repeat.freq === 'daily' ? 86400000 * every : r.repeat.freq === 'weekly' ? 7 * 86400000 * every : null;
    if (!step) { if (at >= fromMs && at <= toMs) out.push({ ...r, at }); continue; }
    const untilMs = r.repeat.until ? midnight(r.repeat.until) + 86400000 : Infinity;
    let t = r.at;
    if (t < fromMs) t += Math.ceil((fromMs - t) / step) * step;
    for (let n = 0; t <= toMs && t < untilMs && n < 400; t += step, n++) {
      if (r.repeat.freq === 'weekly' && Array.isArray(r.repeat.days) && r.repeat.days.length && !r.repeat.days.includes(new Date(t).getDay())) continue;
      out.push({ ...r, at: t, occurrenceOf: r.id, id: `${r.id}@${t}` });
    }
  }
  for (const a of attached) if (a.at >= fromMs && a.at <= toMs) out.push(a);
  return out.sort((a, b) => a.at - b.at);
}

export const snoozeUntil = (now, minutes) => now + Math.max(1, Number(minutes) || 5) * 60000;

/** Patch for a stored reminder after the person acted on it. */
export function markHandled(reminder, action, { now = Date.now(), minutes = 5 } = {}) {
  const base = { handledAt: now, handledAction: action, updatedAt: now };
  if (action === 'snooze') return { ...base, status: 'snoozed', snoozedUntil: snoozeUntil(now, minutes) };
  // An approval is answered by deciding it — the server closes its reminder. A device only notes it saw it: writing a
  // status from a device that hadn't caught up revived approvals already decided (the "delete project" card, 10-02).
  if (reminder?.source === 'approval' && !['snooze', 'dismiss', 'missed'].includes(action)) return { firedAt: reminder.firedAt || now, updatedAt: now };
  if (action === 'fired') return { status: 'fired', firedAt: now, updatedAt: now };
  // Swiped away / turned off: seen, not answered — still `fired`, but stamped so other devices close their copy.
  if (action === 'dismiss') return { status: 'fired', firedAt: reminder?.firedAt || now, dismissedAt: now, updatedAt: now };
  if (action === 'missed') return { ...base, status: 'missed' };
  if (action === 'cancel') return { ...base, status: 'cancelled' };
  return { ...base, status: reminder?.repeat?.freq ? 'scheduled' : 'done' };   // done / open / call … — a repeating one re-arms itself
}

/**
 * What the native alarm module is handed (modules/ops-alarm setReminders): one flat row per occurrence, with
 * what its buttons need — the event's phone / address for Call / Text / Navigate — since it answers with the
 * app closed. `key` matches the local-notification key so either side can arm the same list.
 */
/** A task's own reminder is `tk:<taskId>:at` — its link may point elsewhere (task.opens), the task id is here. */
export const taskOfVirtual = (v) => { const m = /^tk:(.+):at$/.exec(String(v || '')); return m ? m[1] : null; };

/** The full-screen reminder's jump button, by what the reminder points at (the app's opener routes each one). */
export const OPEN_LABELS = { approval: 'Review', task: 'Open task', note: 'Open note', project: 'Open job', event: 'Open event', block: 'Open plan', routine: 'Start routine', process: 'Start routine', page: 'Go there', url: 'Open link', app: 'Open app' };

function approvalAlert(a) {
  const what = String(a.preview || '').trim();
  const why = String(a.note || '').trim();
  return { decide: true, kicker: 'APPROVAL NEEDED', ...(what ? { title: what } : {}), body: why || null, askedBy: a.agent || null, why: why || null };
}

export function alarmPayload(occurrences = [], { events = [] } = {}) {
  const byId = new Map(events.map((e) => [e.id, e]));
  return occurrences.map((r) => {
    const ev = r.link?.type === 'event' ? byId.get(r.link.id) : null;
    return {
      key: `${r.id}|${r.at}`,
      reminderId: r.virtual ? null : (r.occurrenceOf || r.id),
      virtualId: r.virtual ? r.id : null,
      title: r.title || 'Reminder',
      body: r.body || null,
      at: r.at,
      style: REMINDER_STYLES.includes(r.style) ? r.style : 'banner',
      linkType: r.link?.type || null,
      linkId: r.link?.id || null,
      snooze: Array.isArray(r.snooze) && r.snooze.length ? r.snooze : SNOOZE_DEFAULT,
      phone: r.phone || ev?.phone || null,
      address: r.address || ev?.address || null,
      speak: r.speak || null,
      // An approval is reviewed in the app (unlocked), never ticked off from the lock screen.
      openLabel: OPEN_LABELS[r.link?.type] || null,
      hideDone: r.link?.type === 'approval' || r.link?.type === 'routine',
      // A routine's own full-screen look: accent colour, emoji, kicker line, the steps; "Not today" beside Start.
      ...(r.look ? { accent: r.look.color || null, icon: r.look.icon || null, kicker: r.look.kicker || null, steps: Array.isArray(r.look.steps) ? r.look.steps : [], sound: r.look.sound || null } : {}),
      ...(r.routineId ? { routineId: r.routineId, routineDate: r.date || null, routineN: r.n || 1, skipLabel: 'Not today' } : {}),
      // An agent action held for approval: decided right on the alert — what it does as the headline, who asked,
      // their why (older APKs ignore these and show the title / body).
      ...(r.link?.type === 'approval' ? approvalAlert(r.approval || {}) : {}),
    };
  });
}

/**
 * An action taken on the native side → what to do with it here: the reminder-doc action (stored reminders only)
 * and whether to open what it points at. Tapping or swiping the notice = it fired and was seen, not answered.
 */
/** The time a native key "<id>|<at>[~<repeat>]" rang for (its first ring, before any snooze). */
const occurrenceAt = (key) => { const k = String(key || '').split('~')[0]; const n = Number(k.slice(k.lastIndexOf('|') + 1)); return Number.isFinite(n) && n > 0 ? n : null; };

export function alarmActionPlan(a) {
  if (!a || !a.action) return null;
  // Answered after the phone's stored copy was gone: only the key came back — its id is the part before "|at".
  if (a.orphan && !a.reminderId && !a.virtualId && a.key) {
    const id = String(a.key).split('~')[0].replace(/\|[^|]*$/, '');
    a = { ...a, ...(id.includes(':') ? { virtualId: id } : { reminderId: id }) };
  }
  const action = a.action === 'open' || a.action === 'start' ? 'fired' : a.action;   // dismiss stays dismiss (markHandled stamps dismissedAt)
  const rt = routineOfVirtual(a.virtualId);
  // Approve / Deny on an approval alert: the decision goes to the server (it closes the reminder everywhere).
  if ((a.action === 'approve' || a.action === 'reject') && a.linkType === 'approval' && a.linkId) {
    return { reminderId: a.reminderId || null, action: 'fired', at: Number(a.at) || null, decide: { approvalId: a.linkId, action: a.action }, routine: null, open: null, task: null };
  }
  return {
    reminderId: a.reminderId || null,
    action,
    // A routine ring: Start (= open) starts the run and opens its mode; Not today skips the run.
    routine: rt && (a.action === 'open' || a.action === 'start') ? { ...rt, start: true }
      : rt && a.action === 'skip' ? { ...rt, skip: true } : null,
    minutes: a.action === 'snooze' ? Number(a.minutes) || 5 : undefined,
    at: Number(a.at) || null,
    open: a.action === 'open' || a.action === 'start' ? { link: a.linkType && a.linkId ? { type: a.linkType, id: a.linkId } : null, reminderId: a.reminderId || null, action: 'open' } : null,
    // A task's own reminder (tk:<id>:at): Done finishes the task, snooze moves its reminder time.
    // Any other ring without a doc (event / block / routine) that was answered: recorded so the other devices close it.
    answer: a.virtualId && !taskOfVirtual(a.virtualId) && ['done', 'snooze', 'dismiss', 'skip'].includes(a.action)
      ? { virtualId: String(a.virtualId), action: a.action, ...(a.action === 'snooze' ? { until: snoozeUntil(Number(a.at) || Date.now(), a.minutes) } : {}),
        ...(a.linkType && a.linkId ? { link: { type: a.linkType, id: a.linkId } } : {}), ...(occurrenceAt(a.key) ? { at: occurrenceAt(a.key) } : {}), ...(a.title ? { title: a.title } : {}) } : null,
    task: taskOfVirtual(a.virtualId) && ['done', 'snooze', 'dismiss'].includes(a.action)
      ? { id: taskOfVirtual(a.virtualId), ...(a.action === 'done' ? { complete: true }
        : a.action === 'snooze' ? { patch: { remindTime: snoozeUntil(Number(a.at) || Date.now(), a.minutes) } }
          : { patch: { remindDismissedAt: Number(a.at) || Date.now() } }) }
      : null,
  };
}

/**
 * Reminders showing on this device (the alarm module's live list) that were answered somewhere else — done,
 * snoozed, cancelled, turned off — so this device closes its copy too. Being merely shown elsewhere doesn't count.
 *   live: [{ key, reminderId, virtualId, linkType, linkId, at }]   reminders: stored docs   tasks: task docs
 *   answers: reminderAnswers docs (answerWrites) — the answers to rings that have no doc
 * Returns the keys to close.
 */
export function answeredElsewhere(live = [], { reminders = [], tasks = [], answers = [] } = {}) {
  const rById = new Map(reminders.map((r) => [r.id, r]));
  const tById = new Map(tasks.map((t) => [t.id, t]));
  const aByV = new Map(answers.filter((a) => a && a.virtualId).map((a) => [a.virtualId, a]));
  const out = [];
  for (const l of live) {
    if (!l || !l.key) continue;
    const at = Number(l.at) || 0;
    if (l.reminderId) {
      const r = rById.get(l.reminderId);
      if (!r) { if (!String(l.key).startsWith('test|')) out.push(l.key); continue; }   // deleted elsewhere
      if (r.source === 'approval' && approvalStatus(r) !== 'pending') { out.push(l.key); continue; }   // decided (anywhere)
      if (['done', 'cancelled', 'missed'].includes(r.status)) out.push(l.key);
      else if (r.status === 'snoozed' && at < (Number(r.snoozedUntil) || 0)) out.push(l.key);        // not the snoozed repeat itself
      else if (r.status === 'scheduled' && r.handledAt && r.handledAt >= at) out.push(l.key);          // a repeat answered: re-armed for next time
      else if (r.dismissedAt && r.dismissedAt >= at) out.push(l.key);
      continue;
    }
    // An event / block / routine ring has no doc of its own: its answer (reminderAnswers) closes every copy that was
    // ringing before it was answered — a later occurrence (or this device's own snoozed repeat) is newer.
    const ans = l.virtualId ? aByV.get(l.virtualId) : null;
    if (ans && Number(ans.answeredAt) >= at) { out.push(l.key); continue; }
    const taskId = taskOfVirtual(l.virtualId);
    if (taskId) {
      const t = tById.get(taskId);
      if (!t || t.status !== 'todo') out.push(l.key);                                              // done / deleted
      else if (Number(t.remindTime) !== at && Number(t.remindAt) !== at) out.push(l.key);            // snoozed / moved
      else if (t.remindDismissedAt && Number(t.remindDismissedAt) >= at) out.push(l.key);
    }
  }
  return out;
}

/**
 * Alerts filed by the server for right now (an agent action held for approval): they arrive with `at` already
 * behind us, so they are shown at once instead of armed. One day old at most; shown once (status → fired).
 */
export function dueAlerts(reminders = [], now = Date.now(), { maxAgeMs = 86400000 } = {}) {
  return reminders.filter((r) => r && r.source === 'approval' && r.status === 'scheduled' && approvalStatus(r) === 'pending' && Number.isFinite(r.at) && r.at <= now && now - r.at <= maxAgeMs)
    .sort((a, b) => a.at - b.at);
}

// ---- approvals ---------------------------------------------------------------------------------------------------
// The server decides an approval; the reminder doc it files (agentgate/opsAlert.ts) is only how the alert reaches the
// phone. So whether one is waiting comes from the approval — `approval.status` (the server stamps it when decided) or
// the server's own list — never from the reminder's status, which every device writes (shown, opened, snoozed).
const DECIDED = new Set(['approved', 'executed', 'rejected', 'cancelled', 'failed']);
/** 'pending' | approved | executed | rejected | cancelled | failed — from the reminder doc alone (offline). */
export function approvalStatus(r) {
  const st = r?.approval?.status;
  if (st) return st;
  return DECIDED.has(r?.handledAction) ? r.handledAction : 'pending';
}

/**
 * The Approvals screen's two lists. reminders = the synced approval reminders; server = agentApprovals list rows
 * (null when offline / not loaded) — when given, it is the truth: its status wins, and a pending one with no reminder
 * doc still shows. decided = { [approvalId]: verdict } taken on this screen until the sync brings it.
 * → { waiting: [item], history: [item] }, item = { id, reminderId, preview, fn, agent, note, args, card, promotable, result,
 *   status, at, decidedAt } — card = the server's description (customer, job, amount, lines, link; agentgate/describe.ts)
 */
export function approvalsView({ reminders = [], server = null, decided = {} } = {}) {
  const items = new Map();
  for (const r of reminders) {
    if (!r || r.source !== 'approval' || !r.approval?.id) continue;
    const a = r.approval;
    items.set(a.id, { id: a.id, reminderId: r.id, preview: a.preview || null, fn: a.fn || null, agent: a.agent || null, note: a.note || null, args: a.args || null,
      card: a.card || null, promotable: false, result: null,
      status: approvalStatus(r), at: Date.parse(a.createdAt) || r.createdAt || r.at || 0, decidedAt: r.handledAt || null });
  }
  if (Array.isArray(server)) {
    const onServer = new Set();
    for (const x of server) {
      if (!x?.id) continue;
      onServer.add(x.id);
      const cur = items.get(x.id) || { id: x.id, reminderId: null };
      items.set(x.id, { ...cur, preview: x.preview || cur.preview || null, fn: x.fn || cur.fn || null, agent: x.agent || cur.agent || null, note: x.note || cur.note || null,
        args: x.args ?? cur.args ?? null, card: x.review?.card || cur.card || null, promotable: !!x.promotable, result: x.result ?? null, lastError: x.lastError || null,
        allowedBy: x.allowedBy || null,   // ran under an "always allow" rule — never waited, shown in Decided
        status: x.status || cur.status, at: Date.parse(x.createdAt) || cur.at || 0, decidedAt: Date.parse(x.decidedAt) || cur.decidedAt || null });
    }
    // On this device as waiting but not in the server's recent list: it was decided long ago (the list is newest first).
    for (const [id, it] of items) if (!onServer.has(id) && it.status === 'pending' && server.length >= 50) items.set(id, { ...it, status: 'decided' });
  }
  const all = [...items.values()].map((it) => (decided[it.id] ? { ...it, status: decided[it.id] } : it));
  return {
    waiting: all.filter((it) => it.status === 'pending').sort((a, b) => b.at - a.at),
    history: all.filter((it) => it.status !== 'pending').sort((a, b) => (b.decidedAt || b.at) - (a.decidedAt || a.at)).slice(0, 80),
  };
}

/** Approvals still waiting, newest first — from the reminder docs the server files, so the list works with no signal. */
export function waitingApprovals(reminders = []) {
  return reminders.filter((r) => r && r.source === 'approval' && r.approval && approvalStatus(r) === 'pending')
    .sort((a, b) => (b.createdAt || b.at || 0) - (a.createdAt || a.at || 0));
}

/** "remind me to order trusses @10:45 !" (parseQuickEvent's reminder) → the task it is. null when the line has no time. */
export function reminderTask(parsed, { now = Date.now() } = {}) {
  if (!parsed || parsed.type !== 'reminder' || !parsed.title) return null;
  const at = Number.isFinite(parsed.at) ? parsed.at : now + 3600000;      // no time in the line → an hour from now
  const d = new Date(at);
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { title: parsed.title, scheduledDate: date, remindTime: at, remindStyle: parsed.style || 'banner', tags: parsed.tags || [] };
}

/** Tasks that carry a reminder time, soonest first; `done` = the finished ones, newest first (history). */
export function taskReminders(tasks = [], { done = false } = {}) {
  const list = tasks.filter((t) => t && Number.isFinite(t.remindTime) && (done ? t.status === 'done' : t.status === 'todo'));
  return list.sort((a, b) => (done ? (b.completedAt || 0) - (a.completedAt || 0) : a.remindTime - b.remindTime));
}

// ── Repeats ────────────────────────────────────────────────────────────────────────────────────────────────────────
// A stored reminder's `repeat` is { freq: 'daily' | 'weekly', every?, days?: [0-6, Sun = 0], until? } — nextOccurrences
// expands it; markHandled re-arms it. These read and write it from what people type and pick.
const DOW = { sun: 0, sunday: 0, mon: 1, monday: 1, tue: 2, tues: 2, tuesday: 2, wed: 3, weds: 3, wednesday: 3, thu: 4, thur: 4, thurs: 4, thursday: 4, fri: 5, friday: 5, sat: 6, saturday: 6 };
const DOW_RE = '(?:sun(?:day)?|mon(?:day)?|tue(?:s(?:day)?)?|wed(?:s|nesday)?|thu(?:r(?:s(?:day)?)?)?|fri(?:day)?|sat(?:urday)?)';

/** "call mom every sunday 6pm" → { repeat: { freq: 'weekly', every: 1, days: [0] }, rest: 'call mom 6pm' }; no repeat → { repeat: null, rest }. */
export function parseRepeat(text) {
  const s = String(text || '');
  const rules = [
    [/\b(?:every\s+weekday|weekdays|every\s+work\s*day)\b/i, () => ({ freq: 'weekly', every: 1, days: [1, 2, 3, 4, 5] })],
    [/\b(?:every\s+weekend|weekends)\b/i, () => ({ freq: 'weekly', every: 1, days: [0, 6] })],
    [new RegExp(`\\bevery\\s+(${DOW_RE}(?:\\s*(?:,|and|&|\\+)\\s*${DOW_RE})*)\\b`, 'i'), (m) => ({ freq: 'weekly', every: 1, days: [...new Set(m[1].toLowerCase().split(/\s*(?:,|and|&|\+)\s*/).map((d) => DOW[d.trim()]).filter((d) => d != null))].sort() })],
    [/\bevery\s+other\s+day\b/i, () => ({ freq: 'daily', every: 2 })],
    [/\bevery\s+other\s+week\b/i, () => ({ freq: 'weekly', every: 2 })],
    [/\bevery\s+(\d{1,2})\s+days?\b/i, (m) => ({ freq: 'daily', every: Math.max(1, Number(m[1])) })],
    [/\bevery\s+(\d{1,2})\s+weeks?\b/i, (m) => ({ freq: 'weekly', every: Math.max(1, Number(m[1])) })],
    [/\b(?:every\s+day|everyday|daily|each\s+day)\b/i, () => ({ freq: 'daily', every: 1 })],
    [/\b(?:every\s+week|weekly|each\s+week)\b/i, () => ({ freq: 'weekly', every: 1 })],
  ];
  for (const [re, make] of rules) {
    const m = s.match(re);
    if (m) return { repeat: make(m), rest: (s.slice(0, m.index) + s.slice(m.index + m[0].length)).replace(/\s{2,}/g, ' ').trim() };
  }
  return { repeat: null, rest: s.trim() };
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** "Daily" · "Every 2 days" · "Weekdays" · "Mon, Thu" · "Weekly" · "Every 2 weeks". */
export function repeatLabel(repeat) {
  if (!repeat?.freq) return '';
  const every = Math.max(1, Number(repeat.every) || 1);
  if (repeat.freq === 'daily') return every === 1 ? 'Daily' : `Every ${every} days`;
  const days = Array.isArray(repeat.days) ? [...repeat.days].sort() : [];
  if (days.join() === '1,2,3,4,5' && every === 1) return 'Weekdays';
  if (days.join() === '0,6' && every === 1) return 'Weekends';
  const base = days.length ? days.map((d) => DAY_NAMES[d]).join(', ') : 'Weekly';
  return every === 1 ? base : `Every ${every} weeks${days.length ? ` · ${base}` : ''}`;
}

/** First time at or after `from` that fits the repeat's weekdays, keeping `at`'s time of day (a new weekly reminder
 *  "every Monday 9am" typed on a Wednesday starts next Monday). */
export function firstRepeatAt(at, repeat, from = Date.now()) {
  let t = at;
  if (t < from) { const d = new Date(t); const f = new Date(from); d.setFullYear(f.getFullYear(), f.getMonth(), f.getDate()); t = d.getTime(); if (t < from) t += 86400000; }
  if (repeat?.freq === 'weekly' && Array.isArray(repeat.days) && repeat.days.length) {
    for (let i = 0; i < 7 && !repeat.days.includes(new Date(t).getDay()); i++) t += 86400000;
  }
  return t;
}

const ANSWER_KEEP_MS = 7 * 86400000;
/** reminderAnswers doc id for a virtual ring (path-safe: the server allows [A-Za-z0-9_.:@-]). */
export const answerId = (virtualId) => String(virtualId || '').replace(/[^A-Za-z0-9_.:@-]/g, '_').slice(0, 120) || 'x';

/**
 * A ring with no doc of its own (an event's / block's / routine's) answered here — done, snoozed, turned off — so the
 * other devices close their copy (answeredElsewhere). Task rings and stored reminders record on their own docs; this
 * is for the rest. Old answers (a week) are swept in the same group. → writes ([] when there is nothing to record).
 */
export function answerWrites({ virtualId, action, until = null, title = null, link = null, at = null, answers = [], now = Date.now() }) {
  if (!virtualId || taskOfVirtual(virtualId) || String(virtualId).startsWith('test')) return [];
  const id = answerId(virtualId);
  // title / link / at: what the desk's Dismissed list shows (and opens) for one set aside for later
  const data = { virtualId, action: action || 'dismiss', answeredAt: now, ...(until ? { until } : {}), ...(title ? { title: String(title).slice(0, 200) } : {}), ...(link?.type && link?.id ? { link: { type: link.type, id: link.id } } : {}), ...(Number.isFinite(at) ? { at } : {}) };
  const writes = [{ op: 'set', path: `reminderAnswers/${id}`, data, merge: false }];
  for (const a of answers) if (a?.id && a.id !== id && Number(a.answeredAt) < now - ANSWER_KEEP_MS) writes.push({ op: 'delete', path: `reminderAnswers/${a.id}` });
  return writes;
}

/**
 * Rings turned off with Dismiss — set aside for later, not done — for the Dismissed list: task reminders (the task still
 * open), stored one-off reminders (fired, not re-armed) and event / block rings (their answers). Newest first.
 * → [{ kind: 'task' | 'stored' | 'answer', id, title, at, dismissedAt, link, task?, reminder?, answer? }]
 */
export function dismissedReminders({ tasks = [], reminders = [], answers = [] } = {}) {
  const out = [];
  for (const t of tasks) {
    if (!t || t.status !== 'todo' || !Number.isFinite(t.remindTime) || !(Number(t.remindDismissedAt) >= t.remindTime)) continue;
    out.push({ kind: 'task', id: t.id, title: t.title, at: t.remindTime, dismissedAt: Number(t.remindDismissedAt), link: t.opens?.type ? t.opens : { type: 'task', id: t.id }, task: t });
  }
  for (const r of reminders) {
    if (!r || r.source === 'approval' || r.status !== 'fired' || !r.dismissedAt || r.repeat) continue;
    out.push({ kind: 'stored', id: r.id, title: r.title, body: r.body || null, at: r.at, dismissedAt: r.dismissedAt, link: r.link || null, reminder: r });
  }
  for (const a of answers) {
    if (!a || a.action !== 'dismiss' || a.cleared) continue;
    out.push({ kind: 'answer', id: a.id, title: a.title || 'Reminder', at: a.at ?? a.answeredAt, dismissedAt: a.answeredAt, link: a.link || null, answer: a });
  }
  return out.sort((x, y) => (y.dismissedAt || 0) - (x.dismissedAt || 0));
}

/**
 * Waiting approvals asked in one go: the same agent, each within `gapMs` of the one before — so a working session
 * (an invoice, then its payments) is approved in one tap, oldest first. → [{ agent, ids, items }] with 2+ items.
 */
export function approvalSessions(waiting = [], { gapMs = 3 * 3600000 } = {}) {
  const byAgent = new Map();
  for (const it of [...waiting].sort((a, b) => a.at - b.at)) {
    const k = it.agent || 'agent';
    const runs = byAgent.get(k) || [];
    const last = runs[runs.length - 1];
    if (last && it.at - last.items[last.items.length - 1].at <= gapMs) last.items.push(it);
    else runs.push({ agent: k, items: [it] });
    byAgent.set(k, runs);
  }
  return [...byAgent.values()].flat().filter((g) => g.items.length > 1).map((g) => ({ ...g, ids: g.items.map((x) => x.id) }));
}

/** Where an approval's card links: the thing it made once it ran (the new invoice — to review and send), else the card's. */
export function approvalLink(item, appBase = 'https://app.battlebooks.ca') {
  const made = item?.result && typeof item.result === 'object' ? item.result.invoiceId : null;
  if (made && ['executed', 'approved'].includes(item.status)) return { label: `Open ${item.result.invoiceNumber || 'the invoice'} to review and send`, url: `${appBase}/invoices/view?id=${encodeURIComponent(made)}` };
  return item?.card?.link || null;
}

// ---- approval bursts -------------------------------------------------------------------------------------------
// An agent asks for a run of approvals in one go (12 time moves): the first rings as usual — full screen, decided
// right there — and the rest join ONE quiet stacked alert ("11 more approvals from Willow") instead of ringing one
// after another. A burst = each approval within `windowMs` of the one before (Lj, 2026-10-08).
export const APPROVAL_BURST_MS = 3 * 60000;

/**
 * burst = { at, items: [{ id, title, agent }] } | null (what this device kept) → { ring, burst }.
 * ring: true = ring this one on its own (it starts a new burst); false = add it to the stacked alert.
 */
export function approvalBurstStep(burst, item, now = Date.now(), { windowMs = APPROVAL_BURST_MS } = {}) {
  const it = { id: item?.id || null, title: String(item?.title || '').trim() || 'Approval needed', agent: item?.agent || null };
  const live = burst && Array.isArray(burst.items) && burst.items.length && Number.isFinite(burst.at) && now - burst.at <= windowMs;
  if (!live) return { ring: true, burst: { at: now, items: [it] } };
  if (it.id && burst.items.some((x) => x.id === it.id)) return { ring: false, burst: { ...burst, at: now }, repeat: true };
  return { ring: false, burst: { at: now, items: [...burst.items, it] } };
}

/** The stacked alert's text: the ones after the first (which rang on its own). */
export function approvalStackText(burst) {
  const rest = (burst?.items || []).slice(1);
  const n = rest.length;
  const agents = [...new Set(rest.map((x) => x.agent).filter(Boolean))];
  const who = agents.length === 1 ? ` from ${agents[0]}` : '';
  const shown = rest.slice(-3).reverse().map((x) => x.title);
  return {
    count: n,
    title: `${n} more approval${n === 1 ? '' : 's'}${who}`,
    body: `${shown.join(' · ')}${n > 3 ? ` · +${n - 3} more` : ''} — tap to review each one`,
  };
}
